import { fmtDateTime, fmtDayLong, PRIZE_SHEET } from '../campaign';
import { Crop, PrizeArt } from './Shared';
import { GoldBarsIcon } from './Icons';

/* ------------------------------------------------------------------
   Contents of the hub's bottom sheets (prototype openSheet): the full
   prize list, the terms and the grand draw results. Each is rendered
   inside <Sheet> by index.js.
   ------------------------------------------------------------------ */

/* The grand prizes in the prototype's hierarchy — hero card, two featured
   cards, the "Also up for grabs" grid, then rows — plus the Gold and Bitcoin
   every other chest wins. The list itself is the API's (`prizes.all`); the
   layout (campaign.js PRIZE_SHEET) only decides where each prize sits, and
   any prize it doesn't name falls into the rows. */
export function PrizesList({ prizes, drawOn }) {
  const { total, all } = prizes;
  const byName = new Map(all.map((g) => [g.name, g]));
  const pick = (names) => names.map((n) => byName.get(n)).filter(Boolean);
  const hero = byName.get(PRIZE_SHEET.hero);
  const featured = pick(PRIZE_SHEET.featured);
  const also = pick(PRIZE_SHEET.also);
  const placed = new Set([PRIZE_SHEET.hero, ...PRIZE_SHEET.featured, ...PRIZE_SHEET.also]);
  const rows = pick(PRIZE_SHEET.rows).concat(all.filter((g) => !placed.has(g.name) && !PRIZE_SHEET.rows.includes(g.name)));

  return (
    <>
      <div className="gq-sh-title">Golden Chest Prizes</div>
      <div className="gq-sh-sub">
        Every Gold Chest wins. {total} grand prizes are drawn on {fmtDayLong(drawOn)}, and every other chest wins Gold or Bitcoin.
      </div>

      {hero && (
        <div className="gq-ptop1">
          <PrizeArt prize={hero} className="gq-him" />
          <div className="gq-pbadge">Grand prize</div>
          <div className="gq-pn">{hero.name}</div>
          <div className="gq-pd">{hero.sub}</div>
        </div>
      )}
      {featured.length > 0 && (
        <div className="gq-ptop2">
          {featured.map((p) => (
            <div className="gq-pcard" key={p.name}>
              <PrizeArt prize={p} className="gq-him" />
              <div className="gq-pbadge">Grand prize</div>
              <div className="gq-pn">{p.name}</div>
              <div className="gq-pd">{p.sub}</div>
            </div>
          ))}
        </div>
      )}

      {also.length > 0 && (
        <>
          <div className="gq-rsec gq-tight">Also up for grabs</div>
          <div className="gq-pmid">
            {also.map((p) => (
              <div className="gq-pcard gq-sm" key={p.name}>
                <PrizeArt prize={p} className="gq-mim" />
                <div className="gq-pn">{p.name}</div>
                <div className="gq-pd">{p.sub}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="gq-rsec gq-tight">More prizes</div>
      <div className="gq-plist">
        {rows.map((p) => (
          <div className="gq-prow" key={p.name}>
            <PrizeArt prize={p} />
            <div>
              <div className="gq-pn">{p.name}</div>
              <div className="gq-pd">{p.sub}</div>
            </div>
          </div>
        ))}
        <div className="gq-prow">
          <div className="gq-rank gq-tier"><GoldBarsIcon /></div>
          <div>
            <div className="gq-pn">Gold</div>
            <div className="gq-pd">24K digital gold</div>
          </div>
        </div>
        <div className="gq-prow">
          <div className="gq-rank gq-tier gq-btc">₿</div>
          <div>
            <div className="gq-pn">Bitcoin</div>
            <div className="gq-pd">Credited as Sats</div>
          </div>
        </div>
      </div>

      <div className="gq-note">Gem rewards are separate and instant.</div>
    </>
  );
}

/* one block of a terms section: a paragraph, a bullet list or a table */
function TermBlock({ block }) {
  if (block.p) return <p className="gq-tp">{block.p}</p>;
  if (block.ul) {
    return (
      <ul className="gq-tul">
        {block.ul.map((item, i) => (
          <li key={i}>
            {item && typeof item === 'object'
              ? <><b>{item.lead}</b> {item.text}</>
              : item}
          </li>
        ))}
      </ul>
    );
  }
  if (block.table) {
    const { head = [], rows = [] } = block.table;
    return (
      <table className="gq-ttable">
        <thead><tr>{head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}
        </tbody>
      </table>
    );
  }
  return null;
}

/* The terms: numbered sections (campaign.js TERMS) when the campaign sends
   them, else the older flat list of sentences. */
export function TermsList({ terms }) {
  const { summary, sections, items } = terms;
  return (
    <>
      <div className="gq-sh-title">Terms &amp; Conditions</div>
      {summary ? <div className="gq-sh-sub">{summary}</div> : null}
      {sections && sections.length ? (
        <ol className="gq-tsecs">
          {sections.map((s, i) => (
            <li className="gq-tsec" key={i}>
              <div className="gq-th">{s.title}</div>
              {(s.blocks || []).map((b, j) => <TermBlock key={j} block={b} />)}
            </li>
          ))}
        </ol>
      ) : (
        <ol className="gq-tlist">
          {(items || []).map((t, i) => <li key={i}>{t}</li>)}
        </ol>
      )}
    </>
  );
}

/* grand draw results: how the user did, then every grand winner */
export function WinnersList({ draw, total }) {
  const { you, winners: list, drawnAt } = draw;
  return (
    <>
      <div className="gq-sh-title">Grand draw results</div>
      <div className="gq-sh-sub">
        {total} grand prizes drawn on {fmtDateTime(drawnAt, true)}. Every other chest won Sats or Gold.
      </div>

      <div className="gq-wyou">
        <Crop kind="chest" />
        <div className="gq-wh">{you.title}</div>
        <div className="gq-ws">{you.text}</div>
        <div className="gq-wk">{you.kicker}</div>
      </div>

      <div className="gq-plist">
        {list.map((w, i) => (
          <div className={`gq-wrow${w.isYou ? ' gq-me' : ''}`} key={i}>
            <div className="gq-rk">{i + 1}</div>
            <div>
              <div className="gq-wn">{w.name}</div>
              <div className="gq-wc">{w.city}</div>
            </div>
            <div className="gq-wp">{w.prize}</div>
          </div>
        ))}
      </div>
    </>
  );
}
